import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * PermissionGuard Component
 * Componente de guarda que protege rotas/componentes baseado em permissões granulares
 * Verifica se o usuário tem permissão específica (módulo + ação)
 */
import React, { useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useAuth } from '@contexts/AuthContext';
// ============================================================================
// Component
// ============================================================================
/**
 * PermissionGuard
 *
 * Componente de guarda que verifica permissões granulares do usuário.
 *
 * Sistema de Permissões:
 * - Cada permissão é identificada por: módulo + ação
 * - Exemplo: { modulo: 'usuarios', acao: 'editar' }
 * - As permissões são verificadas via AuthContext.hasPermission()
 *
 * Comportamento:
 * - Sem usuário logado: Redireciona para /app/login
 * - Sem permissão + fallback: Renderiza fallback
 * - Sem permissão + sem fallback: Redireciona para redirectTo
 * - Com permissão: Renderiza children
 *
 * @example
 * ```tsx
 * // Proteger um componente inteiro
 * <PermissionGuard modulo="usuarios" acao="editar">
 *   <UserEditForm />
 * </PermissionGuard>
 *
 * // Com fallback customizado
 * <PermissionGuard
 *   modulo="financeiro"
 *   acao="visualizar"
 *   fallback={<p>Você não tem acesso ao financeiro</p>}
 * >
 *   <FinanceiroDashboard />
 * </PermissionGuard>
 *
 * // Sem toast de erro
 * <PermissionGuard modulo="rh" acao="aprovar" showToast={false}>
 *   <ApprovalButton />
 * </PermissionGuard>
 * ```
 */
const PermissionGuard = ({ children, anyOf, modulo, acao = 'acessar', fallback = null, showToast = true, redirectTo = '/app/inicio', }) => {
    const { hasPermission, user } = useAuth();
    // Se não há usuário logado, redirecionar para login
    if (!user) {
        return _jsx(Navigate, { to: "/app/login", replace: true });
    }
    // Verificar se tem a permissão necessária (memoizado para evitar recálculos)
    const temPermissao = useMemo(() => {
        // Se foi fornecida uma lista de permissões alternativas, basta ter uma delas
        if (Array.isArray(anyOf) && anyOf.length > 0) {
            return anyOf.some(permissao => {
                if (!permissao?.modulo || !permissao?.acao) {
                    console.error('PermissionGuard: permissão inválida em anyOf', permissao);
                    return false;
                }
                return hasPermission(permissao.modulo, permissao.acao);
            });
        }
        // Validar se módulo e ação não são undefined
        if (!modulo || !acao) {
            console.error('PermissionGuard: módulo ou ação não fornecidos', { modulo, acao });
            return false;
        }
        const resultado = hasPermission(modulo, acao);
        return resultado;
    }, [anyOf, hasPermission, modulo, acao]);
    // Mostrar toast de erro em useEffect para evitar problemas de render
    useEffect(() => {
        if (!temPermissao && showToast) {
            const moduloTexto = modulo || 'módulo não especificado';
            if (Array.isArray(anyOf) && anyOf.length > 0) {
                toast.error(`Você não tem permissão para acessar ${moduloTexto}`);
                return;
            }
            const acaoTexto = acao === 'acessar' ? 'acessar' : `${acao} em`;
            toast.error(`Você não tem permissão para ${acaoTexto} ${moduloTexto}`);
        }
    }, [anyOf, temPermissao, showToast, acao, modulo]);
    // Sem permissão - usar fallback ou redirecionar
    if (!temPermissao) {
        // Se tem fallback, usar ele, senão redirecionar
        if (fallback !== null) {
            return _jsx(_Fragment, { children: fallback });
        }
        return _jsx(Navigate, { to: redirectTo, replace: true });
    }
    // Com permissão - renderizar children
    return _jsx(_Fragment, { children: children });
};
export default PermissionGuard;
